import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Eye, EyeOff, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Empty, Inline, Primary, Secondary, Tag, money, useQuery } from "@/components/backoffice/kit";
import { BulkFulfillment, FulfillmentSwitch } from "@/components/account/FulfillmentSwitch";
import ProductEdit from "@/components/backoffice/ProductEdit";
import AddProductSheet from "@/components/backoffice/AddProductSheet";

function Row({ p, isHome, onEye, onMode, onOpen }) {
    const visible = p.active && p.simple_visible !== false;
    const retail = p.consigned && p.customer_price != null ? p.customer_price : p.from_price;
    return (
        <li className="grid grid-cols-[56px_minmax(0,1fr)] gap-3 border border-[#1a1a1a] bg-white p-3 sm:grid-cols-[72px_minmax(0,1fr)_auto_auto]" data-testid={`product-row-${p.id}`}>
            <button onClick={onOpen} className="h-14 w-14 border border-[#767676] sm:h-[72px] sm:w-[72px]" aria-label={`Edit ${p.name}`} data-testid={`product-open-${p.id}`}>
                {p.image && <img src={p.image} alt="" className="h-full w-full object-contain" />}
            </button>
            <button onClick={onOpen} className="min-w-0 text-left" data-testid={`product-name-${p.id}`}>
                <p className="truncate text-[16px] font-semibold">{p.name}</p>
                <p className="text-[15px]">
                    from {money(retail)}{p.you_keep != null && <span className="text-[#4d4d4d]"> · You keep {money(p.you_keep)}</span>}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                    {!visible && <Tag tone="grey">Hidden</Tag>}
                    {p.inherited && <Tag tone="grey">13thav product</Tag>}
                    {p.price_locked && isHome && <Tag tone="ink">Locked price</Tag>}
                </div>
            </button>
            <div className="flex items-center gap-3 sm:justify-self-end">
                <button onClick={() => onEye(p)} aria-pressed={visible} data-testid={`product-eye-${p.id}`}
                    className="inline-flex min-h-[44px] items-center gap-1.5 border border-[#1a1a1a] bg-white px-2 text-[14px] font-medium">
                    {visible ? <><Eye size={16} /> Shown</> : <><EyeOff size={16} /> Hidden</>}
                </button>
            </div>
            {!isHome && (
                <div className="col-span-2 sm:col-span-1 sm:w-64">
                    <FulfillmentSwitch on={p.fulfillment === "consignment"} onChange={(v) => onMode(p, v)} testid={`product-mode-${p.id}`} />
                </div>
            )}
        </li>
    );
}

export function ReorderCards({ cards, onDone }) {
    const [list, setList] = useState(cards);
    const [drag, setDrag] = useState(null);
    const move = (from, to) => {
        if (to < 0 || to >= list.length) return;
        const next = [...list]; const [it] = next.splice(from, 1); next.splice(to, 0, it); setList(next);
    };
    return (
        <div className="border border-[#1a1a1a] bg-white p-3" data-testid="reorder-cards">
            <p className="text-[16px] font-semibold">Reorder cards</p>
            <p className="text-[14px] text-[#4d4d4d]">Drag a card, or use the arrows. This is the order your shop's grid shows.</p>
            <ul className="mt-2 grid gap-1">
                {list.map((c, n) => (
                    <li key={c.key} draggable onDragStart={() => setDrag(n)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag != null) move(drag, n); setDrag(null); }}
                        className="flex items-center gap-2 border border-[#767676] bg-white px-2" data-testid={`reorder-row-${c.key}`}>
                        <span className="w-6 text-[14px] tabular-nums text-[#4d4d4d]">{n + 1}</span>
                        <span className="flex-1 text-[16px]">{c.title}</span>
                        <button onClick={() => move(n, n - 1)} aria-label="Move up" data-testid={`reorder-up-${c.key}`} className="flex h-11 w-11 items-center justify-center"><ArrowUp size={16} /></button>
                        <button onClick={() => move(n, n + 1)} aria-label="Move down" data-testid={`reorder-down-${c.key}`} className="flex h-11 w-11 items-center justify-center"><ArrowDown size={16} /></button>
                    </li>
                ))}
            </ul>
            <div className="mt-3"><Primary onClick={() => onDone(list.map((c) => c.key))} testid="reorder-done" full>Done</Primary></div>
        </div>
    );
}

/** 8.5 Products — one list, Add product on top, eye + 13thav prints per row, bulk switch, Reorder cards. `?product=kind:id` edits. */
export default function ProductsList() {
    const { params, set, pathname } = useQuery();
    const edit = params.get("product");
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [add, setAdd] = useState(false);
    const [reorder, setReorder] = useState(false);
    const load = useCallback(() => {
        setErr("");
        api.get("/store/catalog").then(({ data: d }) => setData(d)).catch((e) => { setData({ products: [] }); setErr(formatApiError(e.response?.data?.detail)); });
    }, []);
    useEffect(() => { load(); }, [load]);
    const patch = async (p, body, ok) => {
        try {
            const { data: d } = await api.patch(`/store/catalog/${p.kind}/${p.id}`, body);
            setData((s) => ({ ...s, products: s.products.map((x) => (x.id === d.product.id ? { ...x, ...d.product } : x)) }));
            if (ok) toast.success(ok);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const eye = (p) => patch(p, p.active ? { simple_visible: !(p.simple_visible !== false) } : { active: true, simple_visible: true });
    const mode = (p, on) => patch(p, { fulfillment: on ? "consignment" : "self" }, on ? "13thav prints it" : "You print it");
    const bulk = async (m) => {
        try { const { data: d } = await api.post("/store/catalog/fulfillment/bulk", { mode: m }); toast.success(d.note); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const saveOrder = async (keys) => {
        try { await api.put("/store/catalog/order", { card_order: keys }); toast.success("Card order saved"); setReorder(false); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (data === null) return <div className="flex min-h-[30vh] items-center justify-center" data-testid="products-loading"><Loader2 className="animate-spin" /></div>;
    const products = data.products || [];
    const isHome = !!data.is_home;
    if (edit) {
        const [kind, id] = edit.split(":");
        const p = products.find((x) => x.kind === kind && x.id === id);
        const back = pathname;
        if (!p) return <div className="grid gap-3"><Secondary to={back} testid="product-back">Back</Secondary><Empty title="Product not found." /></div>;
        return <ProductEdit product={p} consignment={data.consignment} isHome={isHome} back={back} onSaved={() => { load(); set({ product: null }); }} />;
    }
    const allOn = products.length > 0 && products.filter((p) => p.inherited).every((p) => p.fulfillment === "consignment");
    return (
        <div className="grid gap-4" data-testid="products-list">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h1 className="text-[22px] font-semibold">Products <span className="text-[16px] font-normal text-[#4d4d4d]">{products.length}</span></h1>
                <Primary onClick={() => setAdd(true)} testid="products-add">Add product</Primary>
            </div>
            <Inline error={err} onRetry={load} testid="products-error" />
            {!isHome && products.length > 0 && <BulkFulfillment count={products.length} allOn={allOn} onApply={bulk} rate={data.consignment?.rate} />}
            {products.length === 0 ? (
                <Empty title="No products yet." testid="products-empty"><p className="mt-1 text-[15px]">Add from the 13thav catalog or add your own.</p></Empty>
            ) : (
                <ul className="grid gap-2" data-testid="products-rows">
                    {products.map((p) => <Row key={p.id} p={p} isHome={isHome} onEye={eye} onMode={mode} onOpen={() => set({ product: `${p.kind}:${p.id}` })} />)}
                </ul>
            )}
            {reorder ? <ReorderCards cards={data.cards || []} onDone={saveOrder} />
                : (data.cards || []).length > 1 && <button onClick={() => setReorder(true)} className="min-h-[44px] justify-self-start underline" data-testid="products-reorder">Reorder cards</button>}
            {add && <AddProductSheet isHome={isHome} onAdded={() => { setAdd(false); load(); }} onClose={() => setAdd(false)} />}
        </div>
    );
}
